import {
  HistoryOutlined,
  ClockCircleOutlined,
  PrinterOutlined
} from "@ant-design/icons";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  Divider,
  Modal,
  Table,
  Tabs,
  Tag,
  Typography,
  Skeleton
} from "antd";
import dayjs from "dayjs";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import React, { useState, useCallback } from "react";

import mnjLogo from "@/assets/mnj-logo.jpg";
import { SwitchButton } from "@/components/ui/SwitchButton";
import { features } from "@/config/features";
import { useGetCostSummary } from "@/hooks/cost";
import { useGetMonthlyForecastVersions } from "@/hooks/forecasting";
import { useSubcontractorName } from "@/hooks/useGetSubcontractors";
import { ISelectOption } from "@/types";
import { IForecastingCentralRepoRes } from "@/types/forecasting";
import { formatNumberToGB } from "@/utils/currency";
import { getMaterialPercent } from "@/utils/material";

const { Title, Text } = Typography;

const getVarianceColor = (variance: number) => {
  const roundedVariance = Number(variance.toFixed(2));
  if (roundedVariance > 0) return "#52c41a";
  if (roundedVariance < 0) return "#ff4d4f";
  return "#595959";
};

type IComparisonType = "budget" | "cost";

export const ForecastVsBudget = ({
  selectedProject,
  selectedMonth,
  projectCentralRepoData,
  setIsLatestVersionSelected,
  setLatestVersion,
  selectedVersion,
  setSelectedVersion
}: {
  selectedProject: ISelectOption;
  selectedMonth: string;
  projectCentralRepoLoading: boolean;
  projectCentralRepoData?: IForecastingCentralRepoRes;
  setIsLatestVersionSelected?: (
    isLatestVersionSelected: boolean | null
  ) => void;
  setLatestVersion?: (latestVersion: string | null) => void;
  selectedVersion?: string;
  setSelectedVersion?: (selectedVersion: string) => void;
}) => {
  const [comparisonType, setComparisonType] =
    React.useState<IComparisonType>("budget");

  const {
    isLoading: monthlyForecastVersionsLoading,
    data: monthlyForecastVersionsData
  } = useGetMonthlyForecastVersions(
    selectedProject?.description,
    selectedMonth,
    selectedProject?.opportunityNo
  );

  // Actual incurred costs (Labour, Plant, Material, Subcontractor) for the
  // selected job/month — drives every row's "Cost" column.
  const { data: costSummary } = useGetCostSummary(
    selectedProject?.value,
    selectedMonth
  );

  // Set default selected version to the first one
  React.useEffect(() => {
    if (monthlyForecastVersionsData?.versions?.length) {
      const latestVersionIndex =
        monthlyForecastVersionsData.versions.length - 1;
      setSelectedVersion?.(
        `${monthlyForecastVersionsData.versions[latestVersionIndex].version}`
      );
    }
  }, [setSelectedVersion, monthlyForecastVersionsData]);

  const activeVersion = React.useMemo(() => {
    return monthlyForecastVersionsData?.versions?.find(
      (v) => `${v.version}` === selectedVersion
    );
  }, [monthlyForecastVersionsData, selectedVersion]);

  // Collect unique plot+stage keys and task IDs from the selected forecast version
  const versionTaskIds = React.useMemo(() => {
    if (!activeVersion) return new Set<string>();
    const taskIds = new Set<string>();
    const collectTasks = (
      stagesAndTasks: {
        Plot: string | null;
        Run: string | null;
        Stage: string;
        Tasks: { taskId: string }[];
      }[]
    ) => {
      stagesAndTasks?.forEach((s) => {
        s.Tasks?.forEach((t) => taskIds.add(t.taskId));
      });
    };
    collectTasks(activeVersion.groundworks?.StagesAndTasks ?? []);
    collectTasks(activeVersion.roadAndSewers?.StagesAndTasks ?? []);
    return taskIds;
  }, [activeVersion]);

  // Compute budget from projectCentralRepoData by summing each selected task's own costBreakdown
  const budgetTotals = React.useMemo(() => {
    const totals = { labour: 0, plant: 0, material: 0, subcontractor: 0 };
    if (!projectCentralRepoData || versionTaskIds.size === 0) return totals;

    const sumMatchingTasks = (
      stages: {
        tasks: {
          taskId: string;
          costBreakdown?: {
            labour_rate: number;
            plant_rate: number;
            material_rate: number;
            subcontractor_rate: number;
          } | null;
        }[];
      }[]
    ) => {
      stages?.forEach((stage) => {
        stage.tasks?.forEach((task) => {
          if (versionTaskIds.has(task.taskId) && task.costBreakdown) {
            totals.labour += task.costBreakdown.labour_rate;
            totals.plant += task.costBreakdown.plant_rate;
            totals.material += task.costBreakdown.material_rate;
            totals.subcontractor += task.costBreakdown.subcontractor_rate;
          }
        });
      });
    };

    projectCentralRepoData.groundworks?.plots?.forEach((plot) =>
      sumMatchingTasks(plot.stages)
    );
    projectCentralRepoData.roadsAndSewers?.runs?.forEach((run) =>
      sumMatchingTasks(run.stages)
    );

    return totals;
  }, [projectCentralRepoData, versionTaskIds]);

  // Compute forecast totals from budgetForecasts of the selected version
  const forecastTotals = React.useMemo(() => {
    const totals = { labour: 0, plant: 0, material: 0, subcontractor: 0 };
    if (!activeVersion?.budgetForecasts) return totals;

    const bf = activeVersion.budgetForecasts;
    totals.labour =
      bf.BudgetLabourCosts?.reduce((sum, item) => sum + item.Amount, 0) ?? 0;
    totals.plant =
      bf.BudgetPlantCosts?.reduce((sum, item) => sum + item.Amount, 0) ?? 0;
    totals.material = bf.BudgetMaterialCost?.Amount ?? 0;
    totals.subcontractor =
      bf.BudgetSubContractorCost?.reduce((sum, item) => sum + item.Amount, 0) ??
      0;

    return totals;
  }, [activeVersion]);

  // Variation total from selected version
  const variationTotal = React.useMemo(() => {
    if (!activeVersion?.variations?.length) return 0;
    return activeVersion.variations.reduce(
      (sum, v) => sum + v.UtilizedAmount,
      0
    );
  }, [activeVersion]);

  const tableData = React.useMemo(() => {
    const buildRow = (
      key: string,
      category: string,
      forecast: number,
      budget: number,
      cost: number
    ) => ({
      key,
      category,
      forecast,
      budget,
      cost,
      variance:
        comparisonType === "budget" ? budget - forecast : forecast - cost
    });

    return [
      buildRow(
        "labour",
        "Labour",
        forecastTotals.labour,
        budgetTotals.labour,
        costSummary?.labour || 0
      ),
      buildRow(
        "plant",
        "Plant",
        forecastTotals.plant,
        budgetTotals.plant,
        costSummary?.plant || 0
      ),
      buildRow(
        "material",
        "Material",
        forecastTotals.material,
        budgetTotals.material,
        costSummary?.material || 0
      ),
      buildRow(
        "subcontractor",
        "Subcontractor",
        forecastTotals.subcontractor,
        budgetTotals.subcontractor,
        costSummary?.subcontractor || 0
      )
    ];
  }, [comparisonType, forecastTotals, budgetTotals, costSummary]);

  // Print logic
  type PrintSection =
    | "labourCosts"
    | "plantEquipment"
    | "materials"
    | "subcontractors"
    | "plotDetail"
    | "forecastVsBudget";

  const PRINT_SECTIONS: { key: PrintSection; label: string }[] = [
    { key: "labourCosts", label: "Labour Costs" },
    { key: "plantEquipment", label: "Plant & Equipment" },
    { key: "materials", label: "Materials" },
    { key: "subcontractors", label: "Subcontractors" },
    { key: "plotDetail", label: "Plot Detail" },
    { key: "forecastVsBudget", label: "Forecast vs Tender Budget Summary" }
  ];

  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [selectedPrintSections, setSelectedPrintSections] = useState<
    Set<PrintSection>
  >(new Set(["labourCosts"]));
  const [isPrinting, setIsPrinting] = useState(false);

  const latestVersion = monthlyForecastVersionsData?.versions?.length
    ? monthlyForecastVersionsData.versions[
        monthlyForecastVersionsData.versions.length - 1
      ]
    : null;
  const isLatestVersionSelected =
    latestVersion && selectedVersion === `${latestVersion.version}`;

  React.useEffect(() => {
    if (
      setIsLatestVersionSelected &&
      typeof isLatestVersionSelected === "boolean" &&
      typeof selectedVersion === "string"
    )
      setIsLatestVersionSelected(!!isLatestVersionSelected);
  }, [isLatestVersionSelected, selectedVersion, setIsLatestVersionSelected]);

  // Report the latest version up to the parent (always the newest version,
  // independent of which version tab is currently selected).
  React.useEffect(() => {
    setLatestVersion?.(latestVersion ? `${latestVersion.version}` : null);
  }, [latestVersion, setLatestVersion]);

  const togglePrintSection = (section: PrintSection) => {
    setSelectedPrintSections((prev) => {
      const next = new Set(prev);
      if (next.has(section)) {
        next.delete(section);
      } else {
        next.add(section);
      }
      return next;
    });
  };

  const getSubcontractorName = useSubcontractorName();

  const handlePrint = useCallback(async () => {
    if (selectedPrintSections.size === 0 || !activeVersion) return;
    setIsPrinting(true);

    try {
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4"
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - margin * 2;

      const costSections: PrintSection[] = [
        "labourCosts",
        "plantEquipment",
        "materials",
        "subcontractors"
      ];
      const hasAnyCostSection = costSections.some((s) =>
        selectedPrintSections.has(s)
      );
      const hasPlotDetail = selectedPrintSections.has("plotDetail");
      const hasForecastVsBudget = selectedPrintSections.has("forecastVsBudget");

      const addElementToPdf = async (
        element: HTMLElement,
        startNewPage: boolean,
        yOffset: number = margin
      ) => {
        const canvas = await html2canvas(element, {
          scale: 2,
          logging: false,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff"
        });
        const imgData = canvas.toDataURL("image/png");
        const imgHeight = (canvas.height * contentWidth) / canvas.width;

        if (startNewPage) {
          pdf.addPage();
        }

        const availableHeight = pageHeight - margin - yOffset;

        pdf.addImage(imgData, "PNG", margin, yOffset, contentWidth, imgHeight);
        let remainingHeight = imgHeight - availableHeight;

        while (remainingHeight > 0) {
          pdf.addPage();
          const srcY = imgHeight - remainingHeight;
          pdf.addImage(
            imgData,
            "PNG",
            margin,
            margin - srcY,
            contentWidth,
            imgHeight
          );
          remainingHeight -= pageHeight - margin * 2;
        }
      };

      const container = document.createElement("div");
      container.style.position = "absolute";
      container.style.left = "-9999px";
      container.style.top = "0";
      container.style.width = "800px";
      container.style.background = "#ffffff";
      container.style.padding = "24px";
      container.style.fontFamily = "Arial, sans-serif";
      document.body.appendChild(container);

      const formatMonth = (m?: string) => {
        if (!m) return "";
        try {
          const year = m.slice(0, 4);
          const mon = m.slice(-2);
          const date = new Date(Number(year), Number(mon) - 1);
          return date.toLocaleDateString("en-GB", {
            month: "long",
            year: "numeric"
          });
        } catch {
          return m;
        }
      };

      const bf = activeVersion.budgetForecasts;
      const totalLabour =
        bf?.BudgetLabourCosts?.reduce((sum, item) => sum + item.Amount, 0) ?? 0;
      const totalPlant =
        bf?.BudgetPlantCosts?.reduce((sum, item) => sum + item.Amount, 0) ?? 0;
      const totalMaterial = bf?.BudgetMaterialCost?.Amount ?? 0;
      const totalSubcontractor =
        bf?.BudgetSubContractorCost?.reduce(
          (sum, item) => sum + item.Amount,
          0
        ) ?? 0;

      const headerHtml = `
        <div style="margin-bottom:20px">
          <img src="${mnjLogo}" alt="M&J Logo" style="height:100px;margin-bottom:8px" />
          <div style="font-size:20px;font-weight:bold;color:#1e1e1e">${activeVersion.projectName || "Forecast Summary"}</div>
          ${selectedMonth ? `<div style="font-size:13px;color:#646464;margin-top:4px">Forecast Month: ${formatMonth(selectedMonth)}</div>` : ""}
        </div>
      `;

      let isFirstSection = true;

      // Cost sections
      if (hasAnyCostSection) {
        const costContainer = document.createElement("div");
        costContainer.innerHTML = headerHtml;

        if (
          selectedPrintSections.has("labourCosts") &&
          bf?.BudgetLabourCosts?.length > 0
        ) {
          costContainer.innerHTML += `
            <div style="margin-bottom:20px">
              <h3 style="color:#1890ff;margin-bottom:8px">Labour Costs</h3>
              <table style="width:100%;border-collapse:collapse;font-size:13px">
                <thead>
                  <tr style="background:#fafafa;border-bottom:2px solid #e8e8e8">
                    <th style="padding:8px;text-align:left">Person</th>
                    <th style="padding:8px;text-align:right">Rate/Day</th>
                    <th style="padding:8px;text-align:right">Days</th>
                    <th style="padding:8px;text-align:right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${bf.BudgetLabourCosts.map(
                    (c) => `
                    <tr style="border-bottom:1px solid #f0f0f0">
                      <td style="padding:8px">${c.Name || "-"}</td>
                      <td style="padding:8px;text-align:right">£${c.RatePerDay.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td style="padding:8px;text-align:right">${c.NoOfDays}</td>
                      <td style="padding:8px;text-align:right;color:#1890ff;font-weight:600">£${c.Amount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  `
                  ).join("")}
                </tbody>
                <tfoot>
                  <tr style="background:#e6f7ff;font-weight:bold">
                    <td colspan="3" style="padding:8px">Labour Total</td>
                    <td style="padding:8px;text-align:right;color:#1890ff">£${totalLabour.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          `;
        }

        if (
          selectedPrintSections.has("plantEquipment") &&
          bf?.BudgetPlantCosts?.length > 0
        ) {
          costContainer.innerHTML += `
            <div style="margin-bottom:20px">
              <h3 style="color:#FA8C16;margin-bottom:8px">Plant & Equipment</h3>
              <table style="width:100%;border-collapse:collapse;font-size:13px">
                <thead>
                  <tr style="background:#fafafa;border-bottom:2px solid #e8e8e8">
                    <th style="padding:8px;text-align:left">Equipment</th>
                    <th style="padding:8px;text-align:left">Unit</th>
                    <th style="padding:8px;text-align:right">Unit Cost</th>
                    <th style="padding:8px;text-align:right">Days</th>
                    <th style="padding:8px;text-align:right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${bf.BudgetPlantCosts.map(
                    (c) => `
                    <tr style="border-bottom:1px solid #f0f0f0">
                      <td style="padding:8px">${c.Name || "-"}</td>
                      <td style="padding:8px">${c.UnitOfMeasure || "-"}</td>
                      <td style="padding:8px;text-align:right">£${c.UnitCost.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td style="padding:8px;text-align:right">${c.NoOfDays}</td>
                      <td style="padding:8px;text-align:right;color:#FA8C16;font-weight:600">£${c.Amount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  `
                  ).join("")}
                </tbody>
                <tfoot>
                  <tr style="background:#fff7e6;font-weight:bold">
                    <td colspan="4" style="padding:8px">Plant Total</td>
                    <td style="padding:8px;text-align:right;color:#FA8C16">£${totalPlant.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          `;
        }

        if (selectedPrintSections.has("materials") && bf?.BudgetMaterialCost) {
          costContainer.innerHTML += `
            <div style="margin-bottom:20px">
              <h3 style="color:#22C55E;margin-bottom:8px">Materials</h3>
              <table style="width:100%;border-collapse:collapse;font-size:13px">
                <thead>
                  <tr style="background:#fafafa;border-bottom:2px solid #e8e8e8">
                    <th style="padding:8px;text-align:left">Item</th>
                    <th style="padding:8px;text-align:left">Type</th>
                    <th style="padding:8px;text-align:right">% of Estimate</th>
                    <th style="padding:8px;text-align:right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style="border-bottom:1px solid #f0f0f0">
                    <td style="padding:8px">Bulk Material</td>
                    <td style="padding:8px">Bulk Material</td>
                    <td style="padding:8px;text-align:right">${getMaterialPercent(bf.BudgetMaterialCost.Amount, budgetTotals.material)}%</td>
                    <td style="padding:8px;text-align:right;color:#22C55E;font-weight:600">£${bf.BudgetMaterialCost.Amount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                </tbody>
                <tfoot>
                  <tr style="background:#f0fdf4;font-weight:bold">
                    <td colspan="3" style="padding:8px">Materials Total</td>
                    <td style="padding:8px;text-align:right;color:#22C55E">£${totalMaterial.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          `;
        }

        if (
          selectedPrintSections.has("subcontractors") &&
          bf?.BudgetSubContractorCost?.length > 0
        ) {
          costContainer.innerHTML += `
            <div style="margin-bottom:20px">
              <h3 style="color:#7C3AED;margin-bottom:8px">Subcontractors</h3>
              <table style="width:100%;border-collapse:collapse;font-size:13px">
                <thead>
                  <tr style="background:#fafafa;border-bottom:2px solid #e8e8e8">
                    <th style="padding:8px;text-align:left">Name</th>
                    <th style="padding:8px;text-align:right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  ${bf.BudgetSubContractorCost.map(
                    (c) => `
                    <tr style="border-bottom:1px solid #f0f0f0">
                      <td style="padding:8px">${c.Name ? getSubcontractorName(c.Name) : "-"}</td>
                      <td style="padding:8px;text-align:right;color:#7C3AED;font-weight:600">£${c.Amount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  `
                  ).join("")}
                </tbody>
                <tfoot>
                  <tr style="background:#f5f3ff;font-weight:bold">
                    <td style="padding:8px">Subcontractors Total</td>
                    <td style="padding:8px;text-align:right;color:#7C3AED">£${totalSubcontractor.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          `;
        }

        if (costContainer.innerHTML.trim()) {
          container.innerHTML = "";
          container.appendChild(costContainer);
          await addElementToPdf(container, false);
          isFirstSection = false;
        }
      }

      // Plot Detail
      if (hasPlotDetail) {
        const plotContainer = document.createElement("div");
        plotContainer.style.padding = "0";

        const stageColors: Record<string, string> = {
          "Cut/Fill": "#1890ff",
          "Foundation - Exc & Conc": "#52c41a",
          "Foundation - Walls to DPC": "#faad14",
          "Foundation Floor": "#eb2f96",
          "Private Drives to Binder": "#722ed1",
          "Private Drives to Surface Course": "#13c2c2",
          "Shared Drives to Surface Course": "#fa541c",
          "Plot Services": "#2f54eb",
          "Service Connections": "#a0d911",
          "Plot Paving": "#f5222d",
          "Scaffold Platform": "#1890ff",
          "Topsoil/Plot Clearance": "#52c41a",
          "Screen Walls": "#faad14"
        };

        // Build plot assignments from version groundworks data
        const plotMap = new Map<string, string[]>();
        activeVersion.groundworks?.StagesAndTasks?.forEach((s) => {
          if (s.Plot) {
            if (!plotMap.has(s.Plot)) plotMap.set(s.Plot, []);
            if (!plotMap.get(s.Plot)!.includes(s.Stage)) {
              plotMap.get(s.Plot)!.push(s.Stage);
            }
          }
        });

        let plotHtml = isFirstSection ? headerHtml : "";
        plotHtml += `<h3 style="color:#0891b2;margin-bottom:16px">Plot Details</h3>`;
        plotHtml += `<div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px">`;

        plotMap.forEach((stages, plotNo) => {
          plotHtml += `
            <div style="border:1px solid #e5e7eb;border-radius:8px;padding:12px">
              <div style="font-weight:600;margin-bottom:4px">Plot ${plotNo}</div>
              <div style="font-size:12px;color:#6b7280;margin-bottom:8px">${stages.length} stages</div>
              <div style="border-top:1px solid #f3f4f6;padding-top:8px">
                <div style="font-size:11px;color:#9ca3af;text-transform:uppercase;margin-bottom:4px">Stages Selected</div>
                <div style="display:flex;flex-wrap:wrap;gap:4px">
                  ${stages
                    .map(
                      (s) =>
                        `<span style="background:${stageColors[s] || "#1890ff"};color:#fff;padding:2px 8px;border-radius:4px;font-size:11px">${s}</span>`
                    )
                    .join("")}
                </div>
              </div>
            </div>
          `;
        });
        plotHtml += `</div>`;

        plotContainer.innerHTML = plotHtml;
        container.innerHTML = "";
        container.appendChild(plotContainer);
        await addElementToPdf(container, !isFirstSection);
        isFirstSection = false;
      }

      // Forecast vs Budget Summary
      if (hasForecastVsBudget) {
        const forecastContainer = document.createElement("div");
        forecastContainer.style.padding = "0";

        const forecastTotal =
          totalLabour +
          totalPlant +
          totalMaterial +
          totalSubcontractor +
          variationTotal;
        const bTotal =
          budgetTotals.labour +
          budgetTotals.plant +
          budgetTotals.material +
          budgetTotals.subcontractor +
          variationTotal;
        const totalVariancePrint = bTotal - forecastTotal;

        const rows = [
          {
            label: "Labour",
            forecast: totalLabour,
            budget: budgetTotals.labour
          },
          { label: "Plant", forecast: totalPlant, budget: budgetTotals.plant },
          {
            label: "Material",
            forecast: totalMaterial,
            budget: budgetTotals.material
          },
          {
            label: "Subcontractor",
            forecast: totalSubcontractor,
            budget: budgetTotals.subcontractor
          }
        ];

        // Variation row is shown in dev (flag on) and hidden in production.
        if (features.showVariationRow) {
          rows.push({
            label: "Variation",
            forecast: variationTotal,
            budget: variationTotal
          });
        }

        const fmt = (v: number) =>
          `£${v.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        const varColor = (v: number) =>
          v > 0 ? "#52c41a" : v < 0 ? "#ff4d4f" : "#595959";

        let tblHtml = isFirstSection ? headerHtml : "";
        tblHtml += `
          <h3 style="margin-bottom:8px">Forecast vs Tender Budget Summary</h3>
          <p style="color:#8c8c8c;font-size:12px;margin-bottom:12px">Comparison between forecasted costs and original budget/cost from tender data</p>
          <table style="width:100%;border-collapse:collapse;font-size:13px">
            <thead>
              <tr style="background:#fafafa;border-bottom:2px solid #e8e8e8">
                <th style="padding:8px;text-align:left">Category</th>
                <th style="padding:8px;text-align:right">Forecast</th>
                <th style="padding:8px;text-align:right">Tender Budget</th>
                <th style="padding:8px;text-align:right">Variance</th>
                <th style="padding:8px;text-align:right">Variance %</th>
              </tr>
            </thead>
            <tbody>
        `;

        rows.forEach((r) => {
          const variance = r.budget - r.forecast;
          const pct =
            r.budget !== 0
              ? Math.abs((variance / r.budget) * 100).toFixed(2)
              : "0.00";
          tblHtml += `
            <tr style="border-bottom:1px solid #f0f0f0">
              <td style="padding:8px;font-weight:600">${r.label}</td>
              <td style="padding:8px;text-align:right;color:#1890ff;font-weight:600">${fmt(r.forecast)}</td>
              <td style="padding:8px;text-align:right;color:#595959;font-weight:600">${fmt(r.budget)}</td>
              <td style="padding:8px;text-align:right;color:${varColor(variance)};font-weight:600">${fmt(Math.abs(variance))}</td>
              <td style="padding:8px;text-align:right;color:${varColor(variance)};font-weight:600">${pct}%</td>
            </tr>
          `;
        });

        const totalPct =
          bTotal !== 0
            ? Math.abs((totalVariancePrint / bTotal) * 100).toFixed(2)
            : "0.00";

        tblHtml += `
            </tbody>
            <tfoot>
              <tr style="background:#fafafa;font-weight:bold;font-size:14px">
                <td style="padding:8px">Total</td>
                <td style="padding:8px;text-align:right;color:#1890ff">${fmt(forecastTotal)}</td>
                <td style="padding:8px;text-align:right;color:#595959">${fmt(bTotal)}</td>
                <td style="padding:8px;text-align:right;color:${varColor(totalVariancePrint)}">${fmt(Math.abs(totalVariancePrint))}</td>
                <td style="padding:8px;text-align:right;color:${varColor(totalVariancePrint)}">${totalPct}%</td>
              </tr>
            </tfoot>
          </table>
        `;

        forecastContainer.innerHTML = tblHtml;
        container.innerHTML = "";
        container.appendChild(forecastContainer);
        await addElementToPdf(container, !isFirstSection);
      }

      document.body.removeChild(container);
      pdf.save("forecast-summary.pdf");
      setShowPrintDialog(false);
    } catch (error) {
      console.error("Error generating PDF:", error);
    } finally {
      setIsPrinting(false);
    }
  }, [
    selectedPrintSections,
    activeVersion,
    selectedMonth,
    forecastTotals,
    budgetTotals,
    variationTotal,
    getSubcontractorName
  ]);

  return (
    <Card
      className="border-none"
      classNames={{
        body: "!p-4"
      }}
    >
      {monthlyForecastVersionsLoading && <Skeleton loading />}
      {monthlyForecastVersionsData && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <HistoryOutlined
                style={{
                  fontSize: 18,
                  marginRight: 8,
                  color: "#1890ff"
                }}
              />
              <Title level={5} className="!mb-0">
                Forecast Versions
              </Title>
              <Badge
                count={monthlyForecastVersionsData?.versions?.length || 0}
                style={{
                  backgroundColor: "#E6F6FE",
                  color: "#167EF9",
                  marginLeft: 8
                }}
              />
            </div>
            {isLatestVersionSelected && (
              <Button
                type="default"
                icon={<PrinterOutlined />}
                onClick={() => setShowPrintDialog(true)}
                className="flex items-center gap-2"
              >
                Print
              </Button>
            )}
          </div>

          <Tabs
            type="card"
            activeKey={selectedVersion}
            onChange={setSelectedVersion}
            items={[...(monthlyForecastVersionsData?.versions || [])]
              .reverse()
              ?.map((item) => ({
                key: `${item.version}`,
                label: (
                  <span>
                    <ClockCircleOutlined className="mr-1.5" />
                    Version {item.version}
                    <Text
                      type="secondary"
                      style={{ fontSize: 12, marginLeft: 8 }}
                      className="text-xs ml-2"
                    >
                      ({dayjs(item.createdAt).format("DD MMM YYYY HH:mm")})
                    </Text>
                  </span>
                )
              }))}
          />

          <div className="flex justify-between items-center gap-2 mt-3">
            <div>
              <Title level={5}>Forecast vs Tender Budget Summary</Title>
              <Text type="secondary" style={{ display: "block" }}>
                Comparison between forecasted costs and original budget/cost
                from tender data
              </Text>
            </div>
            <SwitchButton
              value={comparisonType}
              options={[
                {
                  value: "budget",
                  label: "Tender Budget"
                },
                {
                  value: "cost",
                  label: "Cost"
                }
              ]}
              onChange={(val) => setComparisonType(val as IComparisonType)}
            />
          </div>

          <div className="mt-4">
            <Table
              dataSource={tableData}
              rowKey="key"
              pagination={false}
              columns={[
                {
                  title: "Category",
                  dataIndex: "category"
                },
                {
                  title: "Forecast",
                  dataIndex: "forecast",
                  align: "right",
                  width: 150,
                  render: (val) => (
                    <Text className="text-[#167ef9]">
                      £{formatNumberToGB(val)}
                    </Text>
                  )
                },
                comparisonType === "budget"
                  ? {
                      title: "Tender Budget",
                      dataIndex: "budget",
                      align: "right",
                      width: 150,
                      render: (val) => (
                        <Text className="text-[#595959]">
                          £{formatNumberToGB(val)}
                        </Text>
                      )
                    }
                  : {
                      title: "Cost",
                      dataIndex: "cost",
                      align: "right",
                      width: 150,
                      render: (val) => (
                        <Text className="text-[#595959]">
                          £{formatNumberToGB(val)}
                        </Text>
                      )
                    },
                {
                  title: "Variance",
                  dataIndex: "variance",
                  align: "right",
                  width: 150,
                  render: (val) => {
                    return (
                      <Text style={{ color: getVarianceColor(val) }}>
                        £{formatNumberToGB(Math.abs(val))}
                      </Text>
                    );
                  }
                },
                {
                  title: "Variance %",
                  key: "variancePercent",
                  align: "right",
                  width: 120,
                  render: (_, record) => {
                    const divisor =
                      comparisonType === "budget" ? record.budget : record.cost;
                    const percent =
                      divisor !== 0 ? (record.variance / divisor) * 100 : 0;
                    return (
                      <Text
                        style={{
                          color: getVarianceColor(record.variance)
                        }}
                      >
                        {Math.abs(percent).toFixed(2)}%
                      </Text>
                    );
                  }
                }
              ]}
              summary={() => {
                const forecastTotal =
                  tableData.reduce((s, r) => s + r.forecast, 0) +
                  variationTotal;
                const budgetTotal =
                  tableData.reduce((s, r) => s + r.budget, 0) + variationTotal;
                const costTotal =
                  tableData.reduce((s, r) => s + r.cost, 0) + variationTotal;
                const varianceTotal =
                  comparisonType === "budget"
                    ? budgetTotal - forecastTotal
                    : forecastTotal - costTotal;
                const varianceDivisor =
                  comparisonType === "budget" ? budgetTotal : costTotal;
                const varianceTotalPercentage =
                  varianceDivisor !== 0
                    ? (varianceTotal / varianceDivisor) * 100
                    : 0;

                return (
                  <Table.Summary>
                    {/* start::Variation — shown in dev, hidden in production */}
                    {features.showVariationRow && (
                      <Table.Summary.Row>
                        <Table.Summary.Cell index={0}>
                          <Text>Variation</Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={1} align="right">
                          <Text className="text-[#167ef9]">
                            £{formatNumberToGB(variationTotal)}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={2} align="right">
                          <Text className="text-[#595959]">
                            £{formatNumberToGB(variationTotal)}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={3} align="right">
                          <Text className="text-[#595959]">
                            £{formatNumberToGB(0)}
                          </Text>
                        </Table.Summary.Cell>
                        <Table.Summary.Cell index={4} align="right">
                          <Text className="text-[#595959]">0.00%</Text>
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                    {/* end::Variation */}

                    {/* start::Total Row */}
                    <Table.Summary.Row style={{ background: "#f8fafc" }}>
                      <Table.Summary.Cell index={0}>
                        <Text strong>Total</Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={1} align="right">
                        <Text strong className="text-[#167ef9]">
                          £{formatNumberToGB(forecastTotal)}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={2} align="right">
                        <Text strong className="text-[#595959]">
                          £
                          {formatNumberToGB(
                            comparisonType === "budget"
                              ? budgetTotal
                              : costTotal
                          )}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={3} align="right">
                        <Text
                          strong
                          style={{
                            color: getVarianceColor(varianceTotal)
                          }}
                        >
                          £{formatNumberToGB(Math.abs(varianceTotal))}
                        </Text>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right">
                        <Text
                          strong
                          style={{
                            color: getVarianceColor(varianceTotal)
                          }}
                        >
                          {Math.abs(varianceTotalPercentage).toFixed(2)}%
                        </Text>
                      </Table.Summary.Cell>
                    </Table.Summary.Row>
                    {/* end::Total Row */}
                  </Table.Summary>
                );
              }}
            />
            <div className="p-4 bg-[#fffbea] rounded-b-lg">
              <Text strong className="text-[#1e1e1e]">
                Note:
              </Text>
              <Text className="ml-2 text-[#787878]">
                Budget values are calculated from the cost breakdown of the
                selected tasks across all stages and plots. These values
                represent the original budgeted costs for Labour, Plant,
                Materials, and Subcontractors assigned to the selected tasks.
              </Text>
            </div>
          </div>
        </div>
      )}

      {/* Print Dialog Modal */}
      <Modal
        title="Print"
        open={showPrintDialog}
        onCancel={() => setShowPrintDialog(false)}
        footer={[
          <Button
            key="cancel"
            size="large"
            onClick={() => setShowPrintDialog(false)}
          >
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            size="large"
            loading={isPrinting}
            disabled={selectedPrintSections.size === 0}
            onClick={handlePrint}
          >
            Save
          </Button>
        ]}
        width={448}
        centered
      >
        <Text type="secondary">
          Please Select the fields that you would like to print
        </Text>

        <Divider style={{ margin: "12px 0" }} />

        <div className="flex flex-col gap-1">
          {PRINT_SECTIONS.map((section) => (
            <div
              key={section.key}
              className="py-2 border-b border-gray-100 last:border-b-0"
            >
              <Checkbox
                checked={selectedPrintSections.has(section.key)}
                onChange={() => togglePrintSection(section.key)}
              >
                {section.label}
              </Checkbox>
            </div>
          ))}
        </div>

        {selectedPrintSections.size > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {PRINT_SECTIONS.filter((s) => selectedPrintSections.has(s.key)).map(
              (section) => (
                <Tag
                  key={section.key}
                  closable
                  onClose={() => togglePrintSection(section.key)}
                >
                  {section.label}
                </Tag>
              )
            )}
          </div>
        )}
      </Modal>
    </Card>
  );
};
